import { useContext } from "react";
import { differenceInDays, differenceInMinutes, format, getDate } from "date-fns";
import { enGB } from "date-fns/locale";
import { toast } from "saphir";
import { z } from "zod";

import {
    addShift,
    AddShiftParams,
    deleteShift,
    DeleteShiftParams,
    updateShift as patchShift,
    UpdateShiftParams,
} from "@lib/api";
import { deleteShifts } from "@lib/api/deleteShifts";
import { bulkshiftCreatedShift } from "@lib/events/analytics";
import { BulkshiftCreatedShiftPayload } from "@lib/events/generated";
import { i18n, i18nDK } from "@lib/i18n";
import { queries } from "@lib/queries";
import trackEvent from "@lib/trackers";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { PostingTrackingContext } from "../context/PostingTrackingContext";

export const recurrenceOptions = z.enum(["recurrence-all", "recurrence-following"]).optional();
export type RecurrenceOptions = z.infer<typeof recurrenceOptions>;

export function useShiftAPI(mode?: "creation" | "edition" | "deletion") {
    const queryClient = useQueryClient();
    const { data: company } = useQuery(queries.company.detail());
    // Send tracker for VMS task posting edited
    const { sendTracker } = useContext(PostingTrackingContext);

    const invalidateTaskSidersCache = (taskID: string) => {
        const hasMatchingTaskId = (queryKey: readonly unknown[]) => {
            const params = queryKey[2];
            return (
                typeof params === "object" &&
                params !== null &&
                "taskId" in params &&
                params.taskId === taskID
            );
        };

        queryClient.removeQueries({
            queryKey: ["sider", "list"],
            predicate: (query) => {
                return hasMatchingTaskId(query.queryKey);
            },
        });
        queryClient.removeQueries({
            queryKey: ["sider", "prospectList"],
            predicate: (query) => {
                return hasMatchingTaskId(query.queryKey);
            },
        });
    };

    const { mutate: createShift, isPending: isCreateShiftPending } = useMutation({
        mutationFn: addShift,
        onSuccess: (_, variables) => {
            sendTracker(true);
            queryClient.invalidateQueries({ queryKey: ["task"] });
            invalidateTaskSidersCache(variables.taskId);
            showToast();

            void bulkshiftCreatedShift.track({
                ...formatPayloadForTracker(variables.shift),
                industry: company?.organisation?.industry ?? "",
                taskId: variables.taskId,
            });
        },
    });

    const { mutate: updateShift, isPending: isUpdateShiftPending } = useMutation({
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        mutationFn: ({ taskId, params }: { taskId: string; params: UpdateShiftParams }) =>
            patchShift(params),
        onSuccess: (_, variables) => {
            sendTracker(true);
            queryClient.invalidateQueries({ queryKey: ["task"] });
            invalidateTaskSidersCache(variables.taskId);
            showToast();

            trackEvent({
                name: `bulkshift - edited shift`,
                params: {
                    ...formatPayloadForTracker(variables.params.shift),
                    industry: company?.organisation?.industry ?? "",
                    taskId: variables.taskId,
                },
            });
        },
    });

    const { mutate: removeShift, isPending: isRemoveShiftPending } = useMutation({
        mutationFn: ({
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            taskId,
            shiftId,
            scope,
        }: { taskId: string } & DeleteShiftParams & Pick<AddShiftParams, "shift">) =>
            deleteShift({ shiftId, scope }),
        onSuccess: (_, variables) => {
            sendTracker(true);
            queryClient.invalidateQueries({ queryKey: ["task"] });
            invalidateTaskSidersCache(variables.taskId);
            showToast();
            trackEvent({
                name: `bulkshift - deleted shift`,
                params: {
                    ...formatPayloadForTracker(variables.shift),
                    industry: company?.organisation?.industry ?? "",
                    taskId: variables.taskId,
                },
            });
        },
    });

    const { mutate: removeShifts, isPending: isRemoveShiftsPending } = useMutation({
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        mutationFn: ({ taskId, ids }: { taskId: string; ids: string[] }) => {
            return deleteShifts({ ids });
        },
        onSuccess: (_, variables) => {
            sendTracker(true);
            queryClient.invalidateQueries({ queryKey: ["task"] });
            invalidateTaskSidersCache(variables.taskId);

            trackEvent({
                name: `bulkshift - deleted shifts`,
                params: {
                    taskId: variables.taskId,
                    shiftsDeleted: variables.ids.length,
                    industry: company?.organisation?.industry ?? "",
                },
            });

            toast.success(i18n.task_shifts_success_toast_deletion());
        },
    });

    function showToast() {
        toast.success(i18nDK(`task_shifts_success_toast_${mode}`));
    }

    return {
        createShift,
        updateShift,
        removeShift,
        removeShifts,
        isPending:
            isCreateShiftPending ||
            isUpdateShiftPending ||
            isRemoveShiftPending ||
            isRemoveShiftsPending,
    };
}

type BulkshiftShiftTrackingPayload = Omit<
    BulkshiftCreatedShiftPayload,
    "industry" | "taskId" | "logAs"
>;

type WeekdayKey =
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday"
    | "sunday";

function isWeekdayKey(value: string): value is WeekdayKey {
    switch (value) {
        case "monday":
        case "tuesday":
        case "wednesday":
        case "thursday":
        case "friday":
        case "saturday":
        case "sunday":
            return true;
        default:
            return false;
    }
}

function rruleDayToWeekdayKey(dayRRulesIndex: number): WeekdayKey | undefined {
    const dayName = enGB.localize
        ?.day(dayRRulesIndex === 6 ? 0 : dayRRulesIndex + 1)
        ?.toLowerCase();

    return dayName && isWeekdayKey(dayName) ? dayName : undefined;
}

function getMonthlyWeekdayPosition(setPos: number): string | undefined {
    switch (setPos) {
        case 1:
            return "first";
        case 2:
            return "second";
        case 3:
            return "third";
        case 4:
            return "fourth";
        case -1:
            return "last";
        default:
            return undefined;
    }
}

function buildWeekdayFlags(days: number[]): Pick<BulkshiftShiftTrackingPayload, WeekdayKey> {
    const flags: Partial<Record<WeekdayKey, true>> = {};

    for (const dayRRulesIndex of days) {
        const weekdayKey = rruleDayToWeekdayKey(dayRRulesIndex);
        if (weekdayKey) {
            flags[weekdayKey] = true;
        }
    }

    return flags;
}

function formatPayloadForTracker(shift: AddShiftParams["shift"]): BulkshiftShiftTrackingPayload {
    const { startDate, endDate, slots, breakDuration, recurrence } = shift;
    const start = new Date(startDate);
    const end = new Date(endDate);

    const payload: BulkshiftShiftTrackingPayload = {
        slots,
        startDate,
        endDate,
        breakDuration,
        shiftDuration: Math.abs(differenceInMinutes(start, end)),
        recurrence: !!recurrence,
    };

    if (!recurrence) {
        return payload;
    }

    const recurrencePayload: BulkshiftShiftTrackingPayload = {
        ...payload,
        recurrenceWithHolidays: !!recurrence.withHolidays,
        recurrenceUntil: recurrence.until,
        recurrenceType: recurrence.type,
        recurrenceInterval: recurrence.interval,
        recurrenceDurationInDays: Math.abs(differenceInDays(start, new Date(recurrence.until))),
        ...(recurrence.days?.length ? buildWeekdayFlags(recurrence.days) : {}),
    };

    if (recurrence.type !== "monthly") {
        return recurrencePayload;
    }

    if (recurrence.setPos?.length) {
        const recurrenceMonthlyWeekdayPosition = getMonthlyWeekdayPosition(recurrence.setPos[0]);
        if (!recurrenceMonthlyWeekdayPosition) {
            return recurrencePayload;
        }

        return {
            ...recurrencePayload,
            recurrenceMonthlyWeekdayPosition,
            recurrenceMonthlyWeekday: format(start, "EEEE", { locale: enGB }).toLowerCase(),
        };
    }

    return {
        ...recurrencePayload,
        recurrenceMonthlySameDate: getDate(start),
    };
}
